<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable';
import { BAccordion, BAccordionItem, BButton, BOverlay } from 'bootstrap-vue-next';
import { computed, onMounted, ref } from 'vue';
import type { LocationQuery } from 'vue-router';
import { useRoute } from 'vue-router';

import type { GetApiV1ManageIssueResponse } from '~/api/generated';
import { fetchManageIssue } from '~/api/manageIssue';
import ErrorState from '~/common/components/ErrorState.vue';
import LabelAction from '~/common/components/labels/LabelAction.vue';
import { MAX_ASSIGNED_ISSUES_BY_LABEL } from '~/common/Constants';
import { gidToId, idToGid } from '~/common/GidUtils';
import { useVueData } from '~/common/useVueData';
import { AssignedIssuesWithLabelsQueryDocument, IssueDetailsQueryDocument } from '~/graphql/types';

import CloseAction from './manage-issue-page/CloseAction.vue';
import LookupIssue from './manage-issue-page/LookupIssue.vue';
import WorkAction from './manage-issue-page/WorkAction.vue';

const route = useRoute();
const { username, gitlabGroups } = useVueData();

const singleQueryValue = (value: LocationQuery[string] | undefined): string | null => (typeof value === 'string' ? value : null);
const issueIid = computed(() => singleQueryValue(route.query.issueIid));
const projectId = computed(() => singleQueryValue(route.query.projectId));
const projectPath = computed(() => singleQueryValue(route.query.projectPath));
const action = computed(() => singleQueryValue(route.query.action));

const { result, loading: issueLoading, error: issueError } = useQuery(
  IssueDetailsQueryDocument,
  () => ({
    issueIid: issueIid.value ?? '',
    projectId: projectId.value ? idToGid('Project', projectId.value) : null,
    projectPath: projectPath.value,
  }),
  () => ({
    enabled: Boolean(issueIid.value && (projectId.value || projectPath.value)),
  }),
);
const assignedIssuesQueries = username === null
  ? []
  : gitlabGroups.map(fullPath => useQuery(
      AssignedIssuesWithLabelsQueryDocument,
      () => ({ fullPath, username }),
      () => ({ fetchPolicy: 'network-only' }),
    ));
const assignedIssuesLoading = computed(() => assignedIssuesQueries.some(({ loading }) => loading.value));
const assignedIssuesError = computed(() => assignedIssuesQueries.some(({ error: queryError }) => queryError.value));
const assignedIssues = computed(() => assignedIssuesQueries.map(({ result }) => result.value?.group?.issues));

const data = ref<GetApiV1ManageIssueResponse | null>(null);
const error = ref(false);
const fetchData = async (): Promise<void> => {
  try {
    data.value = await fetchManageIssue();
  } catch {
    error.value = true;
  }
};

const project = computed(() => result.value?.projects?.nodes?.[0]);
const issue = computed(() => project.value?.issue);
const assignees = computed(() => (issue.value?.assignees?.nodes ?? [])
  .filter(assignee => assignee !== null)
  .map(({ username: assignee }) => assignee));
const author = computed(() => issue.value?.author.username);
const labels = computed(() => (issue.value?.labels?.nodes ?? [])
  .filter(label => label !== null)
  .map(({ title }) => title));
const groupLabel = computed(() => labels.value.find(label => label.startsWith('group::')));
const productGroup = computed(() =>
  data.value?.groups.find(({ label }) => label === groupLabel.value),
);
const productTeam = computed(() => [
  ...(productGroup.value?.engineering_managers || []),
  ...(productGroup.value?.product_managers || []),
]);
const hasSuppressContributorLinksLabel = computed(() =>
  labels.value.includes('suppress-contributor-links'),
);
const isBlocked = computed(() => Boolean(
  labels.value.includes('workflow::blocked')
  || issue.value?.status?.name === 'Blocked'
  || issue.value?.blocked === true,
));
const assignedIssueCount = computed(() => assignedIssues.value.reduce((sum, issues) => sum + (issues?.count ?? 0), 0));
const assignedIssueCountsByLabel = computed(() => {
  const assignedLabelTitles = assignedIssues.value
    .flatMap(issues => issues?.nodes ?? [])
    .flatMap(issueNode => issueNode?.labels?.nodes ?? [])
    .filter(label => label !== null)
    .map(({ title }) => title);
  const counts = Object.fromEntries(Object.keys(MAX_ASSIGNED_ISSUES_BY_LABEL).map(label => [label, 0]));
  for (const title of assignedLabelTitles) {
    if (title in counts) {
      counts[title] += 1;
    }
  }

  return counts;
});
// The actions only render once data has loaded, so an error without data means the page's own request failed
const pageRequestFailed = computed(() => error.value && !data.value);

const mentions = computed(() => (issue.value ? [issue.value.author.username, ...assignees.value] : []));
const numericProjectId = computed(() => gidToId(project.value?.id)?.toString() ?? '');
const numericGroupId = computed(() => gidToId(project.value?.group?.id)?.toString() ?? '');

onMounted(fetchData);
</script>

<template>
  <LookupIssue v-if="issueIid === null" />
  <ErrorState v-else-if="issueError || pageRequestFailed" />
  <div v-else-if="!issueLoading && !issue">
    This issue could not be found.
  </div>
  <BOverlay
    v-else-if="!project || !issue || !data || assignedIssuesLoading"
    :show="true"
    no-wrap
    fixed
  />
  <template v-else>
    <h1>Manage Issue</h1>
    <div>
      <a
        :href="project.webUrl ?? undefined"
        target="_blank"
      >{{ project.name }}</a>:
      <a
        :href="issue.webUrl"
        target="_blank"
      >{{ issue.title }}</a>
      <br>
      <div>
        Author: <a
          :href="`https://gitlab.com/${author}`"
          target="_blank"
        >@{{ author }}</a>
      </div>
      <div>
        Assignees:
        <a
          v-for="user in assignees"
          :key="user"
          :href="`https://gitlab.com/${user}`"
          target="_blank"
        >
          @{{ user }}
        </a>
      </div>
      <div v-if="productGroup">
        Group: {{ productGroup.name }}
      </div>
      <div v-if="productTeam.length">
        Product team:
        <a
          v-for="user in productTeam"
          :key="user"
          :href="`https://gitlab.com/${user}`"
          target="_blank"
        >
          @{{ user }}
        </a>
      </div>
      <br>
    </div>
    <ErrorState v-if="error || assignedIssuesError" />
    <div v-else-if="issue.state !== 'opened'">
      You can only manage open issues.
    </div>
    <div v-else-if="hasSuppressContributorLinksLabel">
      This issue is not currently open for contribution.
    </div>
    <template v-else-if="data.authorized">
      <BAccordion class="mb-2">
        <BAccordionItem
          title="Label"
          :visible="action === 'label'"
        >
          <LabelAction
            :description="issue.description ?? ''"
            :group-id="numericGroupId"
            :groups="data.groups"
            :iid="issueIid"
            is-issue
            :labels="labels"
            :label-config="data.labels"
            :title="issue.title"
            :project-id="numericProjectId"
            :web-url="issue.webUrl"
            @error="error = true"
          />
        </BAccordionItem>
      </BAccordion>
      <BAccordion class="mb-2">
        <BAccordionItem
          title="Work"
          :visible="action === 'work'"
        >
          <WorkAction
            :assignees="assignees"
            :assigned-issue-count="assignedIssueCount"
            :assigned-issue-counts-by-label="assignedIssueCountsByLabel"
            :is-blocked="isBlocked"
            :labels="labels"
            :issue-iid="issueIid"
            :mentions="mentions"
            :project-id="numericProjectId"
            :web-url="issue.webUrl"
            @error="error = true"
          />
        </BAccordionItem>
      </BAccordion>
      <BAccordion class="mb-2">
        <BAccordionItem
          title="Close"
          :visible="action === 'close'"
        >
          <CloseAction
            :reasons="data.close_reasons"
            :title="issue.title"
            :issue-iid="issueIid"
            :mentions="mentions"
            :project-id="numericProjectId"
            :web-url="issue.webUrl"
            @error="error = true"
          />
        </BAccordionItem>
      </BAccordion>
    </template>
    <div v-else>
      To manage issues you must be:
      <ul>
        <li>An onboarded community member</li>
        <li>A GitLab team member</li>
      </ul>
      <BButton
        variant="primary"
        :to="{
          name: 'home',
          hash: '#start',
          query: { referrer: encodeURIComponent(issue.webUrl) } }
        "
      >
        Start onboarding
      </BButton>
    </div>
  </template>
</template>
